import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Bars3Icon,
    MagnifyingGlassIcon,
    BellIcon,
    PlusIcon,
    UserCircleIcon,
    ChevronDownIcon,
    CogIcon,
    ArrowRightOnRectangleIcon,
    ClockIcon,
    PauseIcon,
    PlayIcon,
    GlobeAltIcon,
    CommandLineIcon
} from '@heroicons/react/24/outline';
import api from '../../config/axios';
import { showError } from '../../utils/swal';
import { getEffectiveRole } from '../../utils/roles';

const GlobalSearchModal = lazy(() => import('../Modals/GlobalSearchModal'));
const QuickAddModal = lazy(() => import('../Modals/QuickAddModal'));
const NotificationsPanel = lazy(() => import('../Notifications/NotificationsPanel'));

const TARGET_WORKDAY_MS = 9 * 60 * 60 * 1000;

/**
 * Live: end = now. Ended (logout / auto-logout): use server totals when present so remaining
 * time does not keep moving with the clock; never use "now" as session end for closed sessions.
 */
function computeWorkdayStats(session, breaks, now) {
    if (!session?.loginTime) {
        return { totalMs: 0, breakMs: 0, workingMs: 0, remainingMs: TARGET_WORKDAY_MS };
    }

    const statusUpper = String(session.status || '').toUpperCase();
    const isSessionLive = !session.logoutTime && !['LOGGED_OUT', 'AUTO_LOGGED_OUT', 'COMPLETED', 'ENDED'].includes(statusUpper);

    if (!isSessionLive) {
        const w = session.workingTimeMs;
        const b = session.breakTimeMs;
        const t = session.totalTimeMs;
        const hasServerTotals =
            (w !== undefined && w !== null && !Number.isNaN(Number(w))) ||
            (t !== undefined && t !== null && !Number.isNaN(Number(t)));

        if (hasServerTotals) {
            const workingMs = Math.max(0, Number(w) || 0);
            const breakMs = Math.max(0, Number(b) || 0);
            const totalMs = Math.max(0, Number(t) || workingMs + breakMs);
            return {
                totalMs,
                breakMs,
                workingMs,
                remainingMs: Math.max(0, TARGET_WORKDAY_MS - workingMs)
            };
        }

        const login = new Date(session.loginTime);
        const end = session.logoutTime
            ? new Date(session.logoutTime)
            : session.updatedAt
              ? new Date(session.updatedAt)
              : login;

        const totalMs = Math.max(0, end.getTime() - login.getTime());
        const breakMsClosed = breaks.reduce((sum, br) => sum + (Number(br.durationMs) || 0), 0);
        const open = breaks.find((br) => !br.breakEnd);
        const openMs = open?.breakStart
            ? Math.max(0, end.getTime() - new Date(open.breakStart).getTime())
            : 0;
        const breakMs = breakMsClosed + openMs;
        const workingMs = Math.max(0, totalMs - breakMs);
        return {
            totalMs,
            breakMs,
            workingMs,
            remainingMs: Math.max(0, TARGET_WORKDAY_MS - workingMs)
        };
    }

    const login = new Date(session.loginTime);
    const end = now > login ? now : login;
    const totalMs = Math.max(0, end.getTime() - login.getTime());
    const breakMsClosed = breaks.reduce((sum, br) => sum + (Number(br.durationMs) || 0), 0);
    const open = breaks.find((br) => !br.breakEnd);
    const openMs = open?.breakStart
        ? Math.max(0, end.getTime() - new Date(open.breakStart).getTime())
        : 0;
    const breakMs = breakMsClosed + openMs;
    const workingMs = Math.max(0, totalMs - breakMs);
    return {
        totalMs,
        breakMs,
        workingMs,
        remainingMs: Math.max(0, TARGET_WORKDAY_MS - workingMs)
    };
}

const TopNavbar = ({ onMenuClick }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [showSearch, setShowSearch] = useState(false);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showTimeMenu, setShowTimeMenu] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);

    const userMenuRef = useRef(null);
    const timeMenuRef = useRef(null);
    const notificationsRef = useRef(null);

    const [now, setNow] = useState(() => new Date());

    const effectiveRole = getEffectiveRole(user) || '';
    const formattedRole = effectiveRole.replace(/_/g, ' ');

    // Fetch unread notification count (optimized to avoid 429)
    const { data: notificationsData } = useQuery({
        queryKey: ['notifications', 'unread-count'],
        queryFn: async () => {
            const response = await api.get('/notifications?limit=1');
            return response.data;
        },
        enabled: !!user,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        staleTime: 60 * 1000
    });

    const unreadCount = notificationsData?.data?.unreadCount || 0;

    // Fetch attendance session data
    const { data: mySessionData, refetch: refetchMySession } = useQuery({
        queryKey: ['sessions', 'my-session'],
        queryFn: async () => {
            const res = await api.get('/sessions/my-session');
            return res.data;
        },
        enabled: !!user,
        retry: false,
        refetchOnWindowFocus: true,
        staleTime: 30 * 1000
    });

    const session = mySessionData?.session || null;
    const breaks = mySessionData?.breaks || [];
    const statusUpper = String(session?.status || '').toUpperCase();
    const isOnBreak = statusUpper === 'ON_BREAK';
    const isActive = !!session?.loginTime && !session?.logoutTime && !['LOGGED_OUT', 'AUTO_LOGGED_OUT', 'COMPLETED', 'ENDED'].includes(statusUpper);

    // Refetch session data whenever attendance popover menu is toggled open
    useEffect(() => {
        if (showTimeMenu && user) {
            refetchMySession();
        }
    }, [showTimeMenu, user, refetchMySession]);

    // Live clock interval (ticks every 1s when active session exists or user is authenticated)
    useEffect(() => {
        if (!user || !session?.loginTime) return;
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, [user, session?.loginTime]);

    // Session ping heartbeat (every 60s when active)
    useEffect(() => {
        if (!user || !isActive) return;
        const i = setInterval(() => {
            api.post('/sessions/ping').catch(() => {});
        }, 60 * 1000);
        return () => clearInterval(i);
    }, [user, isActive]);

    // Handle outside clicks & ESC key
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
            if (timeMenuRef.current && !timeMenuRef.current.contains(event.target)) {
                setShowTimeMenu(false);
            }
            if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
                if (!event.target.closest('[role="dialog"]') && !event.target.closest('.fixed.inset-0')) {
                    setShowNotifications(false);
                }
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setShowUserMenu(false);
                setShowTimeMenu(false);
                setShowNotifications(false);
            }
            // Cmd+K or Ctrl+K for Global Search
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setShowSearch((prev) => !prev);
            }
        };

        if (showUserMenu || showTimeMenu || showNotifications) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [showUserMenu, showTimeMenu, showNotifications]);

    const handleLogout = useCallback(() => {
        logout();
        navigate('/login');
    }, [logout, navigate]);

    const formatDuration = (ms) => {
        const v = Math.max(0, Number(ms) || 0);
        const totalSeconds = Math.floor(v / 1000);
        const hh = Math.floor(totalSeconds / 3600);
        const mm = Math.floor((totalSeconds % 3600) / 60);
        const ss = totalSeconds % 60;
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
    };

    const live = computeWorkdayStats(session, breaks, now);

    const startBreak = useCallback(async () => {
        try {
            await api.post('/sessions/start-break');
            await refetchMySession();
        } catch (e) {
            const msg = e.response?.data?.message || e.message || 'Could not start break';
            showError('Start break failed', msg);
        }
    }, [refetchMySession]);

    const endBreak = useCallback(async () => {
        try {
            await api.post('/sessions/end-break');
            await refetchMySession();
        } catch (e) {
            const msg = e.response?.data?.message || e.message || 'Could not end break';
            showError('End break failed', msg);
        }
    }, [refetchMySession]);

    const handleQuickAdd = useCallback((type) => {
        setShowQuickAdd(false);
        if (type === 'lead') {
            navigate('/leads/create');
        } else if (type === 'bulk_import') {
            navigate('/leads?bulkImport=true');
        } else if (type === 'user') {
            navigate('/users/create');
        } else if (type === 'project') {
            navigate('/projects/create');
        } else if (type === 'report') {
            navigate('/reports');
        }
    }, [navigate]);

    const progressPercentage = Math.min(100, Math.round((live.workingMs / TARGET_WORKDAY_MS) * 100));

    return (
        <>
            <nav className="shrink-0 bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200/80 sticky top-0 z-30">
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 gap-3">
                        {/* Left: Mobile hamburger + Global Search Bar */}
                        <div className="flex items-center flex-1 min-w-0">
                            <button
                                type="button"
                                onClick={onMenuClick}
                                className="md:hidden p-2 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors flex-shrink-0 mr-1"
                                aria-label="Open main sidebar menu"
                            >
                                <Bars3Icon className="h-6 w-6" />
                            </button>

                            {/* Global Search Trigger Input */}
                            <div className="hidden sm:block flex-1 max-w-md min-w-0">
                                <button
                                    type="button"
                                    onClick={() => setShowSearch(true)}
                                    className="w-full flex items-center justify-between pl-3.5 pr-3 py-2 border border-slate-200 rounded-xl bg-slate-50/80 hover:bg-slate-100/80 text-left transition-all duration-150 group shadow-inner"
                                >
                                    <div className="flex items-center min-w-0">
                                        <MagnifyingGlassIcon className="h-4 w-4 text-slate-400 group-hover:text-indigo-600 transition-colors shrink-0 mr-2.5" />
                                        <span className="text-xs text-slate-400 group-hover:text-slate-600 truncate">
                                            Search leads, phone, customers (Cmd+K)...
                                        </span>
                                    </div>
                                    <kbd className="hidden lg:inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-semibold text-slate-400 bg-white border border-slate-200 rounded-md shadow-2xs shrink-0">
                                        <span>⌘K</span>
                                    </kbd>
                                </button>
                            </div>
                        </div>

                        {/* Right side navigation items */}
                        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
                            {/* Website Link */}
                            <a
                                href="https://bookmyacre.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hidden lg:inline-flex items-center px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100/80 hover:bg-slate-200/80 hover:text-slate-900 border border-slate-200 rounded-xl transition-all shadow-2xs"
                                title="Visit BookMyAcre Website"
                            >
                                <GlobeAltIcon className="h-4 w-4 mr-1.5 text-indigo-600" />
                                <span>bookmyacre.com</span>
                            </a>

                            {/* Quick Add Button */}
                            <div className="relative hidden md:block">
                                <button
                                    type="button"
                                    onClick={() => setShowQuickAdd(true)}
                                    className="inline-flex items-center px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold text-xs rounded-xl shadow-sm shadow-indigo-600/25 transition-all duration-150"
                                >
                                    <PlusIcon className="h-4 w-4 mr-1.5 stroke-[2.5]" />
                                    <span>Quick Add</span>
                                </button>
                            </div>

                            {/* Notifications Trigger */}
                            <div className="relative" ref={notificationsRef}>
                                <button
                                    type="button"
                                    onClick={() => setShowNotifications(!showNotifications)}
                                    className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-xl relative transition-colors"
                                    aria-label="Notifications"
                                    aria-expanded={showNotifications}
                                >
                                    <BellIcon className="h-5 w-5" />
                                    {unreadCount > 0 && (
                                        <span className="absolute top-1.5 right-1.5 h-4 w-4 bg-red-500 text-white text-[10px] font-extrabold rounded-full flex items-center justify-center ring-2 ring-white shadow-xs">
                                            {unreadCount > 9 ? '9+' : unreadCount}
                                        </span>
                                    )}
                                </button>
                            </div>

                            {/* Attendance / Workday Timer */}
                            {!!user && (
                                <div className="relative" ref={timeMenuRef}>
                                    <button
                                        type="button"
                                        onClick={() => setShowTimeMenu(!showTimeMenu)}
                                        className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-xl relative transition-colors"
                                        title="Attendance & Working Hours"
                                        aria-label="Attendance & Working Hours"
                                        aria-expanded={showTimeMenu}
                                    >
                                        <ClockIcon className="h-5 w-5" />
                                        {isOnBreak && (
                                            <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 bg-amber-500 rounded-full ring-2 ring-white animate-pulse" />
                                        )}
                                        {isActive && !isOnBreak && (
                                            <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 bg-emerald-500 rounded-full ring-2 ring-white" />
                                        )}
                                    </button>

                                    {/* Attendance Popover Dropdown */}
                                    {showTimeMenu && (
                                        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-slate-200/90 z-50 overflow-hidden text-slate-800 animate-in fade-in zoom-in-95 duration-100">
                                            <div className="px-4 py-3.5 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                                                <div>
                                                    <div className="text-xs font-bold text-slate-900 uppercase tracking-wider">Attendance Status</div>
                                                    <div className="text-[11px] text-slate-500 mt-0.5">
                                                        {session?.loginTime ? `Login: ${new Date(session.loginTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Not logged in'}
                                                    </div>
                                                </div>
                                                <div>
                                                    {isOnBreak ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                                            ON BREAK
                                                        </span>
                                                    ) : isActive ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                                            WORKING
                                                        </span>
                                                    ) : session ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-300">
                                                            LOGGED OUT
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                                                            NOT STARTED
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Progress Bar */}
                                            <div className="px-4 pt-3">
                                                <div className="flex justify-between items-center text-[11px] font-semibold text-slate-500 mb-1">
                                                    <span>9h Target Progress</span>
                                                    <span>{progressPercentage}%</span>
                                                </div>
                                                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-all duration-300"
                                                        style={{ width: `${progressPercentage}%` }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="px-4 py-3 space-y-2">
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-slate-500 font-medium">Working Hours</span>
                                                    <span className="font-mono font-bold text-slate-900">{formatDuration(live.workingMs)}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-slate-500 font-medium">Break Time</span>
                                                    <span className="font-mono font-bold text-slate-900">{formatDuration(live.breakMs)}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-slate-500 font-medium">Remaining to 9h</span>
                                                    <span className={`font-mono font-bold ${live.remainingMs === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                        {formatDuration(live.remainingMs)}
                                                    </span>
                                                </div>

                                                <div className="pt-2 flex items-center gap-2">
                                                    {!isOnBreak ? (
                                                        <button
                                                            type="button"
                                                            onClick={startBreak}
                                                            disabled={!session || !isActive}
                                                            className="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-semibold rounded-xl bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-xs"
                                                        >
                                                            <PauseIcon className="h-4 w-4 mr-1.5" />
                                                            Start Break
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={endBreak}
                                                            className="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors shadow-xs"
                                                        >
                                                            <PlayIcon className="h-4 w-4 mr-1.5" />
                                                            End Break
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowTimeMenu(false);
                                                            handleLogout();
                                                        }}
                                                        className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold rounded-xl bg-slate-900 hover:bg-slate-950 text-white transition-colors shadow-xs"
                                                    >
                                                        <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1.5" />
                                                        Logout
                                                    </button>
                                                </div>

                                                <div className="pt-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowTimeMenu(false);
                                                            navigate('/attendance');
                                                        }}
                                                        className="w-full inline-flex items-center justify-center px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
                                                    >
                                                        View Attendance Logs
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* User Account Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    type="button"
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 p-1.5 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
                                    aria-expanded={showUserMenu}
                                    aria-haspopup="true"
                                    aria-label="User Account Menu"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-indigo-600 to-blue-500 flex items-center justify-center text-white font-bold text-xs shadow-xs border border-indigo-400/30">
                                        {(user?.firstName?.charAt(0) || 'U')}{(user?.lastName?.charAt(0) || '')}
                                    </div>
                                    <div className="hidden sm:block text-left min-w-0">
                                        <div className="text-xs font-bold text-slate-900 truncate">
                                            {user?.firstName || 'User'} {user?.lastName || ''}
                                        </div>
                                        <div className="text-[10px] font-medium text-slate-500 truncate capitalize">
                                            {formattedRole}
                                        </div>
                                    </div>
                                    <ChevronDownIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                </button>

                                {/* User Dropdown Menu */}
                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl border border-slate-200/90 z-50 py-1.5 animate-in fade-in zoom-in-95 duration-100">
                                        <div className="px-4 py-2 border-b border-slate-100">
                                            <p className="text-xs font-bold text-slate-900 truncate">{user?.firstName} {user?.lastName}</p>
                                            <p className="text-[10px] font-medium text-indigo-600 capitalize truncate">{formattedRole}</p>
                                        </div>
                                        <div className="py-1">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowUserMenu(false);
                                                    navigate('/profile');
                                                }}
                                                className="flex items-center w-full px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                                            >
                                                <CogIcon className="h-4 w-4 mr-2.5 text-slate-400" />
                                                My Profile
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowUserMenu(false);
                                                    handleLogout();
                                                }}
                                                className="flex items-center w-full px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
                                            >
                                                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2.5 text-red-500" />
                                                Logout
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Lazy-Loaded Modals and Panels */}
            <Suspense fallback={null}>
                {showSearch && (
                    <GlobalSearchModal
                        isOpen={showSearch}
                        onClose={() => setShowSearch(false)}
                    />
                )}

                {showQuickAdd && (
                    <QuickAddModal
                        isOpen={showQuickAdd}
                        onClose={() => setShowQuickAdd(false)}
                        onAdd={handleQuickAdd}
                    />
                )}

                {showNotifications && (
                    <NotificationsPanel
                        isOpen={showNotifications}
                        onClose={() => setShowNotifications(false)}
                    />
                )}
            </Suspense>
        </>
    );
};

export default TopNavbar;
